(() => {
  'use strict';

  if (window.__CCSWDarkHomeLoaded) return;
  window.__CCSWDarkHomeLoaded = true;

  const SELECTORS = {
    drawer: '[data-ccsw-mobile-drawer]',
    overlay: '[data-ccsw-mobile-overlay]',
    drawerOpen: '[data-ccsw-drawer-open]',
    drawerClose: '[data-ccsw-drawer-close]',
    carousel: '[data-ccsw-carousel]',
    carouselTrack: '[data-ccsw-carousel-track]',
    carouselPrev: '[data-ccsw-carousel-prev]',
    carouselNext: '[data-ccsw-carousel-next]',
    ajaxButton: '[data-ccsw-ajax-add]',
    cartCount: '[data-ccsw-cart-count]',
    cartTotal: '[data-ccsw-cart-total]',
    toast: '[data-ccsw-toast]',
    toastMessage: '[data-ccsw-toast-message]'
  };

  const bodyLockClass = 'ccsw-drawer-is-open';
  let toastTimer;

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function showToast(message) {
    const toast = document.querySelector(SELECTORS.toast);
    if (!toast) return;

    const messageNode = toast.querySelector(SELECTORS.toastMessage);
    if (messageNode) messageNode.textContent = message;

    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 4200);
  }

  function formatCartTotal(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function updateCartSummary(cart) {
    const count = cart.item_count || 0;
    document.querySelectorAll(SELECTORS.cartCount).forEach((node) => {
      node.textContent = String(count);
      node.hidden = count < 1;
      node.setAttribute('aria-label', `${count} item${count === 1 ? '' : 's'} in cart`);
    });
    document.querySelectorAll(SELECTORS.cartTotal).forEach((node) => {
      node.textContent = formatCartTotal(cart.total_price, cart.currency);
    });
  }

  async function refreshCartCount() {
    const response = await fetch(`${getShopifyRoot()}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    const cart = await response.json();
    updateCartSummary(cart);
    document.dispatchEvent(new CustomEvent('ccsw:cart-updated', { detail: { cart } }));
    return cart;
  }

  function initializeDrawer(root = document) {
    root.querySelectorAll(SELECTORS.drawer).forEach((drawer) => {
      if (drawer.dataset.ccswInitialized === 'true') return;
      drawer.dataset.ccswInitialized = 'true';

      const sectionRoot = drawer.closest('.ccsw') || document;
      const overlay = sectionRoot.querySelector(SELECTORS.overlay) || document.querySelector(SELECTORS.overlay);
      const openButtons = sectionRoot.querySelectorAll(SELECTORS.drawerOpen);
      const closeButtons = drawer.querySelectorAll(SELECTORS.drawerClose);
      let lastFocusedElement = null;

      const openDrawer = () => {
        lastFocusedElement = document.activeElement;
        drawer.classList.add('is-open');
        overlay?.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        document.documentElement.classList.add(bodyLockClass);
        document.body.style.overflow = 'hidden';
        window.setTimeout(() => drawer.querySelector(SELECTORS.drawerClose)?.focus(), 50);
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        overlay?.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        document.documentElement.classList.remove(bodyLockClass);
        document.body.style.overflow = '';
        lastFocusedElement?.focus?.();
      };

      openButtons.forEach((button) => button.addEventListener('click', openDrawer));
      closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
      overlay?.addEventListener('click', closeDrawer);

      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeDrawer();

        if (event.key === 'Tab') {
          const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
            .filter((element) => element.offsetParent !== null);
          if (!focusable.length) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    });
  }

  function initializeCarousels(root = document) {
    root.querySelectorAll(SELECTORS.carousel).forEach((carousel) => {
      if (carousel.dataset.ccswInitialized === 'true') return;
      carousel.dataset.ccswInitialized = 'true';

      const track = carousel.querySelector(SELECTORS.carouselTrack);
      const previous = carousel.querySelector(SELECTORS.carouselPrev);
      const next = carousel.querySelector(SELECTORS.carouselNext);
      if (!track) return;

      const getScrollAmount = () => {
        const slide = track.querySelector('.ccsw-carousel__slide');
        if (!slide) return Math.max(track.clientWidth * 0.8, 240);
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
        return slide.getBoundingClientRect().width + gap;
      };

      const updateButtons = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll;
      };

      previous?.addEventListener('click', () => {
        track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      });
      next?.addEventListener('click', () => {
        track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', updateButtons, { passive: true });

      if ('ResizeObserver' in window) {
        new ResizeObserver(updateButtons).observe(track);
      } else {
        window.addEventListener('resize', updateButtons, { passive: true });
      }
      window.setTimeout(updateButtons, 80);
    });
  }

  function initializeAjaxCart(root = document) {
    root.querySelectorAll('form').forEach((form) => {
      const ajaxButton = form.querySelector(SELECTORS.ajaxButton);
      if (!ajaxButton || form.dataset.ccswAjaxInitialized === 'true') return;
      form.dataset.ccswAjaxInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const label = ajaxButton.querySelector('[data-ccsw-add-label]');
        const message = form.querySelector('[data-ccsw-form-message]');
        const originalLabel = label?.textContent || 'Add to cart';

        ajaxButton.disabled = true;
        ajaxButton.classList.add('is-loading');
        if (label) label.textContent = 'Adding…';
        if (message) message.textContent = '';

        try {
          const response = await fetch(`${getShopifyRoot()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added.');

          await refreshCartCount();
          if (label) label.textContent = 'Added';
          if (message) message.textContent = 'Added to your cart.';
          showToast(`${data.product_title || 'Item'} added to cart.`);
          document.dispatchEvent(new CustomEvent('ccsw:item-added', { detail: { item: data } }));
          window.setTimeout(() => {
            if (label) label.textContent = originalLabel;
            if (message) message.textContent = '';
          }, 2400);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'This item could not be added.';
          if (message) message.textContent = errorMessage;
          if (label) label.textContent = originalLabel;
          showToast(errorMessage);
        } finally {
          ajaxButton.disabled = false;
          ajaxButton.classList.remove('is-loading');
        }
      });
    });
  }

  function initialize(root = document) {
    initializeDrawer(root);
    initializeCarousels(root);
    initializeAjaxCart(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const selectedSlide = event.target.closest('.ccsw-carousel__slide');
    selectedSlide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });
})();
